import { useState, useEffect, useRef } from "react";
import {
  Building2,
  Upload,
  Save,
  MapPin,
  Phone,
  Mail,
  Globe,
  Check,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { Logo } from "@/types/media";
import { resolveMediaUrl, useImageBlobUrl } from "@/lib/media";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { AppConfig } from "@/datasources/AppConfig";

const api = new RemoteDataSource("https://api.toolbox.app.br/urbnet");

interface Endereco {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  pais?: string;
}

interface OuvidoriaResponse {
  id?: string;
  nome?: string;
  cnpj?: string;
  arquivada?: boolean;
  geral?: boolean;
  logo?: Logo;
  razaoSocial?: string;
  naturezaJuridica?: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  integracao?: string;
  etiquetas?: string[];
  latitude?: number;
  longitude?: number;
  altitude?: number;
  endereco?: Endereco;
}

interface OuvidoriaForm {
  nome?: string;
  cnpj?: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  naturezaJuridica?: string;
  email?: string;
  telefone?: string;
  geral?: boolean;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  endereco?: Endereco;
  etiquetas?: string[];
  logoUrl?: string;
}

interface Etiqueta {
  id: string;
  nome: string;
  tipo: string;
}

const formatEndereco = (endereco?) => {
  if (!endereco) return "";
  const parts = [
    endereco.logradouro,
    endereco.numero,
    endereco.bairro,
    endereco.cidade,
    endereco.estado,
    endereco.cep,
    endereco.pais,
  ].filter(Boolean);
  return parts.join(", ");
};

const enderecoMudou = (novoEndereco?: Endereco, enderecoOriginal?: Endereco): boolean => {
  if (!novoEndereco && !enderecoOriginal) return false;
  if (!novoEndereco || !enderecoOriginal) return true;
  return (
    novoEndereco.logradouro !== enderecoOriginal.logradouro ||
    novoEndereco.numero !== enderecoOriginal.numero ||
    novoEndereco.bairro !== enderecoOriginal.bairro ||
    novoEndereco.cidade !== enderecoOriginal.cidade ||
    novoEndereco.estado !== enderecoOriginal.estado ||
    novoEndereco.cep !== enderecoOriginal.cep ||
    novoEndereco.pais !== enderecoOriginal.pais
  );
};

export default function OuvidoriaPage() {
  const { isReadOnly } = useAgentePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [ouvidoria, setOuvidoria] = useState<OuvidoriaResponse | null>(null);
  const [form, setForm] = useState<OuvidoriaForm>({
    nome: "",
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    naturezaJuridica: "",
    email: "",
    telefone: "",
    geral: true,
    latitude: 0,
    longitude: 0,
    altitude: 0,
    endereco: {},
    etiquetas: [],
  });
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoBlobUrl = useImageBlobUrl(logoPreview || undefined);
  const [etiquetas, setEtiquetas] = useState<string[]>([]);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Busca os dados da ouvidoria ao carregar
  useEffect(() => {
    fetchOuvidoria();
  }, []);

  // DESABILITADO TEMPORARIAMENTE: refresh on focus/visibility estava sobrescrevendo
  // o logoPreview ao selecionar uma nova imagem (re-fetch zerava o estado).
  // useEffect(() => {
  //   const handleFocus = () => {
  //     fetchOuvidoria();
  //     fetchEtiquetas();
  //   };
  //
  //   const handleVisibility = () => {
  //     if (document.visibilityState === 'visible') {
  //       fetchOuvidoria();
  //       fetchEtiquetas();
  //     }
  //   };
  //
  //   window.addEventListener('focus', handleFocus);
  //   document.addEventListener('visibilitychange', handleVisibility);
  //
  //   return () => {
  //     window.removeEventListener('focus', handleFocus);
  //     document.removeEventListener('visibilitychange', handleVisibility);
  //   };
  // }, []);

  const fetchOuvidoria = async () => {
    setIsLoading(true);
    try {
      const result = await api.get<OuvidoriaResponse>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}`,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        forceRefresh: true,
        serialize: (raw) => {
          const r = raw as Record<string, unknown>;
          // Normaliza wrappers possíveis da API
          if (Array.isArray(r) && r[0]) return r[0] as unknown as OuvidoriaResponse;
          if (r && "id") return r as unknown as OuvidoriaResponse;
          if (r && "data") {
            if (Array.isArray(r.data) && r.data[0]) return r.data[0] as OuvidoriaResponse;
            return r.data as OuvidoriaResponse;
          }
          if (r && "ouvidoria") return r.ouvidoria as OuvidoriaResponse;
          if (r && "item") {
            if (Array.isArray(r.item) && r.item[0]) return r.item[0] as OuvidoriaResponse;
            return r.item as OuvidoriaResponse;
          }
          return r as unknown as OuvidoriaResponse;
        },
      });

      if ("failure" in result) {
        throw new Error(result.failure.message);
      }

      const data = result.success.data;
      
      if (!data) {
        throw new Error("Dados da ouvidoria não encontrados");
      }
      
      setOuvidoria(data);

      // Preenche o formulário com os dados
      setForm({
        nome: data.nome || "",
        cnpj: data.cnpj || "",
        razaoSocial: data.razaoSocial || "",
        nomeFantasia: data.nomeFantasia || "",
        naturezaJuridica: data.naturezaJuridica || "",
        email: data.email || "",
        telefone: data.telefone || "",
        geral: data.geral ?? true,
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
        altitude: data.altitude || 0,
        endereco: data.endereco || {},
        etiquetas: data.etiquetas || [],
        logoUrl: data.logo?.arquivo || "",
      });

      setSelectedCategorias(data.etiquetas || []);
      setEtiquetas(data.etiquetas || []);
      if (data.logo?.arquivo) {
        setLogoPreview(resolveMediaUrl(data.logo.arquivo) || data.logo.arquivo);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar ouvidoria",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setLogoPreview(base64);
        setForm((prev) => ({ ...prev, logoUrl: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCategoria = (id) => {
    setSelectedCategorias((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleInputChange = (field: keyof OuvidoriaForm, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEnderecoChange = (field: keyof Endereco, value: string) => {
    setForm((prev) => ({
      ...prev,
      endereco: { ...(prev.endereco || {}), [field]: value },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = Settings.token?.accessToken || "";

      // 1. Se a logo foi alterada, chamar o endpoint específico para atualizar logo
      // A comparação usa `logoPreview` para detectar mudanças, pois `form.logoUrl` pode
      // estar sincronizado com o valor original após `fetchOuvidoria`.
      const originalLogoUrl = ouvidoria?.logo?.arquivo || "";
      const logoChanged = logoPreview && logoPreview !== originalLogoUrl;

      if (logoChanged) {
        try {
          const logoFormData = new FormData();

          // Converter base64 para Blob
          const base64Data = logoPreview!.split(',')[1];
          const mimeType = logoPreview!.split(',')[0].match(/:(.*?);/)?.[1] || 'image/png';
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          const sliceSize = 512;

          for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }

          const blob = new Blob(byteArrays, { type: mimeType });
          logoFormData.append("arquivo", blob, "logo.png");
          logoFormData.append("date", String(Date.now()));

          const logoUrl = `${Settings.urlBase}/v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/atualizar-logo`;

          const logoResponse = await fetch(logoUrl, {
            method: "POST",
            headers: {
              "Accept": "application/vnd.data.minimal.v1+json",
              "Authorization": `Bearer ${token}`,
            },
            body: logoFormData,
          });

          if (!logoResponse.ok) {
            const errorData = await logoResponse.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.detail || `Erro ${logoResponse.status}: ${logoResponse.statusText}`);
          }

        } catch (error) {
          throw new Error("Erro ao atualizar a logo: " + (error instanceof Error ? error.message : "Erro desconhecido"));
        }
      }
      
      // 2. Atualizar os outros dados da ouvidoria (sem a logo) via JSON Patch
      // (RFC 6902). Só envia operações para os campos que realmente mudaram.
      type JsonPatchOp = { op: "replace"; path: string; value: unknown };
      const operations: JsonPatchOp[] = [];

      const addIfChanged = <K extends keyof OuvidoriaForm>(
        formKey: K,
        path: string,
        transform?: (v: OuvidoriaForm[K]) => unknown,
      ) => {
        const formValue = form[formKey];
        const originalValue = ouvidoria
          ? (ouvidoria as unknown as Record<string, unknown>)[formKey as string]
          : undefined;
        const normalizedForm = formValue === "" ? null : formValue;
        const normalizedOriginal =
          originalValue === "" || originalValue === undefined
            ? null
            : originalValue;
        if (normalizedForm === normalizedOriginal) return;
        operations.push({
          op: "replace",
          path,
          value: transform ? transform(formValue) : formValue,
        });
      };

      addIfChanged("nome", "/nome");
      addIfChanged("razaoSocial", "/razaoSocial");
      addIfChanged("nomeFantasia", "/nomeFantasia");
      addIfChanged("email", "/email");
      addIfChanged("telefone", "/telefone");
      addIfChanged("cnpj", "/cnpj", (v) =>
        typeof v === "string" ? v.replace(/\D/g, "") : v,
      );

      // geral: boolean — comparação direta
      if ((form.geral ?? true) !== (ouvidoria?.geral ?? true)) {
        operations.push({ op: "replace", path: "/geral", value: form.geral });
      }

      // endereço: envia o objeto completo quando algo mudou
      if (enderecoMudou(form.endereco, ouvidoria?.endereco)) {
        operations.push({
          op: "replace",
          path: "/endereco",
          value: form.endereco ?? {},
        });
      }

      // etiquetas: dependem de `geral`
      //  - se a ouvidoria virou geral → enviar etiquetas: null
      //  - se está setorial → enviar a lista atual quando ela mudou
      const etiquetasOriginais = ouvidoria?.etiquetas || [];
      const etiquetasIguais =
        etiquetasOriginais.length === selectedCategorias.length &&
        etiquetasOriginais.every((e) => selectedCategorias.includes(e));

      if (form.geral && !ouvidoria?.geral) {
        operations.push({ op: "replace", path: "/etiquetas", value: null });
      } else if (!form.geral && !etiquetasIguais) {
        operations.push({
          op: "replace",
          path: "/etiquetas",
          value: selectedCategorias,
        });
      }

      // Se nada mudou e a logo também não foi alterada, não bate na API.
      if (operations.length === 0 && !logoChanged) {
        toast({
          title: "Nada para salvar",
          description: "Nenhuma alteração foi feita.",
        });
        setIsSaving(false);
        return;
      }

      // Se só a logo mudou, pula o PATCH dos demais dados.
      if (operations.length === 0) {
        toast({
          title: "Logo atualizada",
          description: "A logo da ouvidoria foi atualizada com sucesso.",
        });
        await fetchOuvidoria();
        setIsSaving(false);
        return;
      }

      // PATCH JSON Patch (RFC 6902). Servidor retorna 204 sem corpo.
      const patchResult = await api.patch<unknown>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}`,
        headers: { "content-type": "application/json-patch+json" },
        body: operations,
      });

      if ("failure" in patchResult) {
        const failure = patchResult.failure as {
          message?: string;
          body?: Record<string, unknown>;
        };
        const errorData = failure.body || {};

        // Extrai mensagens específicas do formato Problem Details
        if (errorData.errors && typeof errorData.errors === "object") {
          const errorMessages = Object.entries(
            errorData.errors as Record<string, unknown>,
          )
            .map(([field, messages]) => {
              const msgArray = Array.isArray(messages) ? messages : [messages];
              const fieldName = field === "nome" ? "Nome da ouvidoria" : field;
              return `${fieldName}: ${msgArray.join(", ")}`;
            })
            .join("\n");
          throw new Error(errorMessages || failure.message || "Erro ao salvar");
        }

        throw new Error(
          (errorData.message as string) ||
            (errorData.detail as string) ||
            failure.message ||
            "Erro ao salvar",
        );
      }

      toast({
        title: "Dados salvos com sucesso",
        description: "As informações da ouvidoria foram atualizadas.",
      });

      // Recarrega os dados
      await fetchOuvidoria();
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Carregando ouvidoria...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="surface-elevated p-6">
        <div className="flex items-start gap-6">
          {/* Logo Upload */}
          <div className="flex-shrink-0">
            <label
              htmlFor="logo-upload-input"
              className="relative block cursor-pointer group"
            >
              <div className="h-28 w-28 rounded-xl bg-surface-container border-2 border-dashed border-border flex items-center justify-center overflow-hidden group-hover:border-primary/60 transition-colors">
                {logoBlobUrl ? (
                  <img
                    src={logoBlobUrl}
                    alt="Logo"
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      console.error("[OuvidoriaPage] Logo img error:", e.currentTarget.src, e);
                    }}
                    onLoad={() => {
                      console.log("[OuvidoriaPage] Logo img loaded:", logoBlobUrl);
                    }}
                  />
                ) : (
                  <Building2 className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
              <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center pointer-events-none group-hover:bg-primary/90 transition-colors shadow-md">
                <Upload className="h-4 w-4" />
              </span>
            </label>
            <input
              id="logo-upload-input"
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoUpload}
            />
            <p className="text-caption text-center mt-2">Logo institucional</p>
          </div>

          {/* Basic Info */}
          <div className="flex-1 space-y-4">
            <div>
              <h2 className="heading-section">{form.nome || "Ouvidoria"}</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Configuração do perfil institucional
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${form.geral ? 'bg-success/10 text-success border border-success/20' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                {form.geral ? "Ouvidoria Geral" : "Ouvidoria Setorial"}
              </span>
              {ouvidoria?.arquivada && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20">
                  Arquivada
                </span>
              )}
              
            </div>
          </div>
        </div>
      </div>

      {/* Form Sections */}
      <div className="surface-elevated p-6 space-y-6">
        <div>
          <h3 className="heading-subsection mb-4">Dados Institucionais</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome Institucional *</Label>
              <Input
                id="nome"
                value={form.nome}
                onChange={(e) => handleInputChange("nome", e.target.value)}
                placeholder="Nome da ouvidoria"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fantasia">Nome Fantasia</Label>
              <Input
                id="fantasia"
                value={form.nomeFantasia}
                onChange={(e) => handleInputChange("nomeFantasia", e.target.value)}
                placeholder="Nome fantasia"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="razao">Razão Social</Label>
              <Input
                id="razao"
                value={form.razaoSocial}
                onChange={(e) => handleInputChange("razaoSocial", e.target.value)}
                placeholder="Razão social"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={form.cnpj}
                onChange={(e) => handleInputChange("cnpj", e.target.value)}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="natureza">Natureza Jurídica</Label>
              <Input
                id="natureza"
                value={form.naturezaJuridica}
                onChange={(e) => handleInputChange("naturezaJuridica", e.target.value)}
                placeholder="Natureza jurídica"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="municipio">Município</Label>
              <Input
                id="municipio"
                value={ouvidoria?.endereco?.cidade || ""}
                disabled
                className="bg-muted"
              />
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="heading-subsection mb-4">Contato</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  className="pl-10"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="telefone"
                  className="pl-10"
                  value={form.telefone}
                  onChange={(e) => handleInputChange("telefone", e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="heading-subsection mb-4">Endereço</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="logradouro">Logradouro</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="logradouro"
                  className="pl-10"
                  value={form.endereco?.logradouro || ""}
                  onChange={(e) => handleEnderecoChange("logradouro", e.target.value)}
                  placeholder="Rua, avenida, etc."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero">Número</Label>
              <Input
                id="numero"
                value={form.endereco?.numero || ""}
                onChange={(e) => handleEnderecoChange("numero", e.target.value)}
                placeholder="Número"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                value={form.endereco?.bairro || ""}
                onChange={(e) => handleEnderecoChange("bairro", e.target.value)}
                placeholder="Bairro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={form.endereco?.cidade || ""}
                onChange={(e) => handleEnderecoChange("cidade", e.target.value)}
                placeholder="Cidade"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Input
                id="estado"
                value={form.endereco?.estado || ""}
                onChange={(e) => handleEnderecoChange("estado", e.target.value)}
                placeholder="UF"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cep">CEP</Label>
              <Input
                id="cep"
                value={form.endereco?.cep || ""}
                onChange={(e) => handleEnderecoChange("cep", e.target.value)}
                placeholder="00000-000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pais">País</Label>
              <Input
                id="pais"
                value={form.endereco?.pais || ""}
                onChange={(e) => handleEnderecoChange("pais", e.target.value)}
                placeholder="País"
              />
            </div>
          </div>
        </div>

        <Separator />

        <Separator />

        <div>
          <h3 className="heading-subsection mb-4">Tipo de Ouvidoria</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div
              className={`p-4 rounded-lg border-2 transition-colors ${
                form.geral ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Ouvidoria Geral</p>
                  <p className="text-caption mt-1">
                    Recebe solicitações de todas as categorias
                  </p>
                </div>
                {form.geral ? (
                  <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-3 w-3 text-primary-foreground" />
                  </div>
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-border" />
                )}
              </div>
            </div>
            <div
              className={`p-4 rounded-lg border-2 transition-colors ${
                !form.geral ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Ouvidoria Setorial</p>
                  <p className="text-caption mt-1">
                    Focada em categorias específicas
                  </p>
                </div>
                {!form.geral ? (
                  <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-3 w-3 text-primary-foreground" />
                  </div>
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-border" />
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {!form.geral && (
          <div>
            <h3 className="heading-subsection mb-4">Categorias Atendidas</h3>
            <p className="text-caption mb-4">
              Selecione as categorias de solicitações que esta ouvidoria atende.
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              {etiquetas.length === 0 ? (
                <div className="col-span-3 text-center text-muted-foreground text-sm">
                  Nenhuma etiqueta encontrada
                </div>
              ) : (
                etiquetas.map((etiqueta) => (
                  <label
                    key={etiqueta}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-surface-container/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedCategorias.includes(etiqueta)}
                      onCheckedChange={() => toggleCategoria(etiqueta)}
                    />
                    <span className="text-sm">{etiqueta}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      {!isReadOnly && (
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={fetchOuvidoria} disabled={isSaving}>
            Cancelar
          </Button>
          <Button className="gap-2" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      )}
    </div>
  );
}
